import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import {
  datasheetsApi,
  useInvalidateDatasheet,
  type OrderPricing,
  type SizeSlot,
} from "@/lib/datasheets";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasheetId: number;
  sizes: SizeSlot[];
  pricesPerOrder: boolean;
}

const EMPTY = [0, 0, 0, 0, 0, 0, 0];

export function NewDatasheetOrderDialog({ open, onOpenChange, datasheetId, sizes, pricesPerOrder }: Props) {
  const navigate = useNavigate();
  const invalidate = useInvalidateDatasheet();

  const [quantities, setQuantities] = useState<number[]>(EMPTY);
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [fitting, setFitting] = useState(0);
  const [po, setPo] = useState("");
  const [pricing, setPricing] = useState<OrderPricing | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const named = useMemo(() => sizes.filter((s) => s.desc), [sizes]);
  const hasSomething = quantities.some((q) => q > 0) || deliveryCount > 0 || fitting > 0;

  // Debounced live preview. Skipped entirely for a prepaid sheet — the server would return zero
  // anyway, and showing "$0.00" reads as "free" rather than "already paid for".
  useEffect(() => {
    if (!pricesPerOrder || !hasSomething) { setPricing(null); return; }

    const handle = setTimeout(() => {
      datasheetsApi
        .pricePreview(datasheetId, {
          sizeQuantities: quantities,
          deliveryChargeCount: deliveryCount,
          prepaidFittingQuantity: fitting,
          deliveryMethodId: null,
        })
        .then(setPricing)
        .catch(() => setPricing(null));
    }, 300);

    return () => clearTimeout(handle);
  }, [datasheetId, pricesPerOrder, hasSomething, quantities, deliveryCount, fitting]);

  const setQuantity = (index: number, value: number) =>
    setQuantities((prev) => prev.map((q, i) => (i === index - 1 ? value : q)));

  async function submit() {
    setSubmitting(true);
    try {
      const draft = await datasheetsApi.createOrder(datasheetId, {
        sizeQuantities: quantities,
        deliveryChargeCount: deliveryCount,
        prepaidFittingQuantity: fitting,
        requestedByContactId: null,
        note: null,
      });

      const result = await datasheetsApi.commitOrder(datasheetId, draft.datasheetUsageId, {
        sizeQuantities: quantities,
        deliveryChargeCount: deliveryCount,
        prepaidFittingQuantity: fitting,
        requestedByContactId: null,
        note: null,
        dueDate: null,
        deliveryMethodId: null,
        deliveryAddressId: null,
        deliveryTo: null,
        deliveryAttention: null,
        doNotLabelAsAllmark: false,
        clientPurchaseOrderNo: po || null,
      });

      invalidate(datasheetId);

      // Over-draw is allowed and warns rather than blocking, so the warnings have to reach the user.
      for (const w of result.warnings) {
        toast.info(w.message);
      }

      toast.success(`Order placed — job ${result.jobReferenceNo}`);
      onOpenChange(false);
      void navigate(`/jobs/${result.jobId}`);
    } catch {
      toast.error("Could not place the order");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New order</DialogTitle></DialogHeader>

        <div className="space-y-3">
          {named.map((s) => (
            <div key={s.index} className="flex items-center gap-3">
              <Label htmlFor={`size-${s.index}`} className="w-32">{s.desc}</Label>
              <Input
                id={`size-${s.index}`}
                type="number"
                min={0}
                value={quantities[s.index - 1] || ""}
                onChange={(e) => setQuantity(s.index, Number(e.target.value) || 0)}
              />
              <span className="w-28 text-xs text-muted-foreground">{s.quantityAvail} available</span>
            </div>
          ))}

          <div className="flex items-center gap-3">
            <Label htmlFor="delivery" className="w-32">Delivery charges</Label>
            <Input id="delivery" type="number" min={0} value={deliveryCount || ""}
              onChange={(e) => setDeliveryCount(Number(e.target.value) || 0)} />
          </div>

          <div className="flex items-center gap-3">
            <Label htmlFor="fitting" className="w-32">Fittings</Label>
            <Input id="fitting" type="number" min={0} value={fitting || ""}
              onChange={(e) => setFitting(Number(e.target.value) || 0)} />
          </div>

          <div className="flex items-center gap-3">
            <Label htmlFor="po" className="w-32">Customer PO</Label>
            <Input id="po" value={po} onChange={(e) => setPo(e.target.value)} />
          </div>

          {pricesPerOrder ? (
            <div className="rounded-md border p-3 text-sm">
              {pricing ? (
                <>
                  <ul className="space-y-1">
                    {pricing.lines.map((l, i) => (
                      <li key={`${l.description}-${i}`} className="flex justify-between">
                        <span>{l.quantity} × {l.description}</span>
                        <span>${l.lineTotal.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 flex justify-between font-medium">
                    <span>Total ex GST</span><span>${pricing.totalExGst.toFixed(2)}</span>
                  </p>
                </>
              ) : (
                <p className="text-muted-foreground">Enter quantities to see a price.</p>
              )}
            </div>
          ) : (
            <p className="rounded-md border p-3 text-sm text-muted-foreground">
              This order is drawn from the prepaid balance — no charge is raised.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={!hasSomething || submitting}>
            Place order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
